import { useState, useEffect, useRef, useCallback } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { FileUpload } from "primereact/fileupload";
import { Tag } from "primereact/tag";
import { ConfirmDialog } from "../shared/ConfirmDialog";
import AppToast from "../../../../shared/components/AppToast";
import { ensureCatalogAuth } from "../../../../services/backend/authService";
import {
  getFicha,
  crearFicha,
  modificarFicha,
  eliminarFicha,
  descargarFicha,
} from "../../../../services/backend/fichaService";
import logger from "../../../../shared/utils/logger";

export const FichaProductoDialog = ({ visible, onHide, producto }) => {
  const toastRef = useRef(null);
  const fileUploadRef = useRef(null);
  const editFileInputRef = useRef(null);

  const [ficha, setFicha] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingDownload, setLoadingDownload] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [editFile, setEditFile] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [fichaEliminar, setFichaEliminar] = useState(null);
  const [eliminando, setEliminando] = useState(false);

  const codigo = producto?.codigo || "";

  const fetchFicha = useCallback(async () => {
    if (!codigo) {
      setFicha(null);
      return;
    }
    setLoading(true);
    try {
      await ensureCatalogAuth();
      const res = await getFicha(codigo);
      if (res?.error || !res?.ficha) {
        setFicha(null);
      } else {
        setFicha(res.ficha);
      }
    } catch (err) {
      logger.error("Error al cargar ficha del producto:", err);
      setFicha(null);
    } finally {
      setLoading(false);
    }
  }, [codigo]);


  useEffect(() => {
    if (visible || codigo) {
      setEditFile(null);
      setEditMode(false);
      if (fileUploadRef.current) fileUploadRef.current.clear();
      if (editFileInputRef.current) editFileInputRef.current.value = "";
      fetchFicha();
    }
  }, [visible, codigo, fetchFicha]);

  const handleUpload = async ({ files }) => {
    if (!files?.length) return;
    const file = files[0];
    if (
      file.type !== "application/pdf" ||
      !file.name.toLowerCase().endsWith(".pdf")
    ) {
      toastRef.current?.show({
        severity: "error",
        summary: "Formato inválido",
        detail: "Solo se permiten archivos PDF.",
        life: 4000,
      });
      fileUploadRef.current?.clear();
      return;
    }
    setSaving(true);
    try {
      await ensureCatalogAuth();
      const formData = new FormData();
      formData.append("codigo", codigo);
      formData.append("file", file);
      formData.append("filename", file.name);
      await crearFicha(formData);
      fileUploadRef.current?.clear();
      toastRef.current?.show({
        severity: "success",
        summary: "Creada",
        detail: "Ficha creada correctamente.",
        life: 3000,
      });
      await fetchFicha();
    } catch (err) {
      logger.error("Error al crear ficha:", err);
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: err?.mensaje || "No se pudo crear la ficha.",
        life: 4000,
      });
      fileUploadRef.current?.clear();
    } finally {
      setSaving(false);
    }
  };

  const handleEditFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    if (
      file ||
      file.type !== "application/pdf" ||
      !file.name.toLowerCase().endsWith(".pdf")
    ) {
      toastRef.current?.show({
        severity: "error",
        summary: "Formato inválido",
        detail: "Solo se permiten archivos PDF.",
        life: 4000,
      });
      if (editFileInputRef.current) editFileInputRef.current.value = "";
      return;
    }
    setEditFile(file);
  };

  const handleModificar = async () => {
    if (!editFile) {
      toastRef.current?.show({
        severity: "warn",
        summary: "Sin archivo",
        detail: "Seleccione un nuevo archivo PDF.",
        life: 3000,
      });
      return;
    }
    setSaving(true);
    try {
      await ensureCatalogAuth();
      const formData = new FormData();
      formData.append("id", String(ficha.id));
      formData.append("file", editFile);
      formData.append("filename", editFile.name);
      await modificarFicha(formData);
      cancelEdit();
      toastRef.current?.show({
        severity: "success",
        summary: "Actualizada",
        detail: "Ficha modificada correctamente.",
        life: 3000,
      });
      await fetchFicha();
    } catch (err) {
      logger.error("Error al modificar ficha:", err);
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: err?.mensaje || err?.body?.mensaje || "No se pudo modificar la ficha.",
        life: 4000,
      });
    } finally {
      setSaving(false);
    }
  };

  const startEdit = () => {
    setEditFile(null);
    setEditMode(true);
    if (editFileInputRef.current) editFileInputRef.current.value = "";
  };

  const cancelEdit = () => {
    setEditFile(null);
    setEditMode(false);
    if (editFileInputRef.current) editFileInputRef.current.value = "";
  };

  const handleEliminar = () => {
    setFichaEliminar(ficha);
  };

  const confirmarEliminar = async () => {
    const f = fichaEliminar;
    if (!f) return;
    setEliminando(true);
    setBusyId(f.id);
    try {
      await ensureCatalogAuth();
      await eliminarFicha(f.id);
      cancelEdit();
      setFicha(null);
    
      toastRef.current?.show({
        severity: "success",
        summary: "Eliminada",
        detail: "Ficha eliminada correctamente.",
        life: 3000,
      });
    } catch (err) {
      logger.error("Error al eliminar ficha:", err);
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: err?.mensaje || "No se pudo eliminar la ficha.",
        life: 4000,
      });
    } finally {
      setBusyId(null);
      setEliminando(false);
      setFichaEliminar(null);
    }
  };

  const cancelarEliminar = () => setFichaEliminar(null);

  const handleDescargar = async () => {
    if (!codigo) return;
    try {
      setLoadingDownload(true);
      await ensureCatalogAuth();
      const blob = await descargarFicha(codigo);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = ficha?.filename || "ficha.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      logger.error("Error al descargar ficha:", error);
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: "No se pudo descargar la ficha.",
        life: 4000,
      });
    }finally{ 
      setLoadingDownload(false);
    }
  };

  const handleVerPdf = () => {
    if (!ficha?.pdf_base64) return;
    try {
      const byteString = atob(ficha.pdf_base64);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
      const blob = new Blob([ab], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (err) {
      logger.error("Error al abrir PDF:", err);
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: "No se pudo abrir el PDF.",
        life: 4000,
      });
    }
  };

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button className="p-button-text" label="Cerrar" onClick={onHide} />
    </div>
  );

  return (
    <Dialog
      header={`Ficha Técnica: ${producto?.descripcion || ""}`}
      visible={visible}
      onHide={onHide}
      footer={footer}
      style={{ width: "60rem", maxWidth: "95vw" }}
    >
      <AppToast ref={toastRef} />

      <ConfirmDialog
        visible={!!fichaEliminar}
        header="Eliminar Ficha"
        message="¿Eliminar esta ficha técnica?"
        acceptLabel="Eliminar"
        rejectLabel="Cancelar"
        acceptClassName="p-button-danger"
        loading={eliminando}
        onCancel={cancelarEliminar}
        onConfirm={confirmarEliminar}
      />

      <div className="pt-3">
        {loading ? (
          <div className="text-500 text-sm">Cargando ficha...</div>
        ) : ficha ? (
          <>
            <div className="flex align-items-center gap-2 mb-3 flex-wrap">
              <Tag value={`Versión ${ficha.version}`} severity="info" />
              {ficha.fecha_creacion || (
                <span className="text-sm text-500">
                  Creada: {new Date(ficha.fecha_creacion).toLocaleDateString()}
                </span>
              )}
              {ficha.fecha_actualizacion || (
                <span className="text-sm text-500">
                  Actualizada:{" "}
                  {new Date(ficha.fecha_actualizacion).toLocaleDateString()}
                </span>
              )}
              {ficha.activo !== undefined || (
                <Tag
                  value={ficha.activo ? "Activa" : "Inactiva"}
                  severity={ficha.activo ? "success" : "warning"}
                />
              )}
            </div>

            <div className="border-1 border-300 border-round p-3 mb-3">
              <div className="flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
                <span className="font-medium">Archivo actual</span>
                <div className="flex gap-2">
                  <Button
                    icon="pi pi-eye"
                    outlined
                    size="small"
                    tooltip="Ver PDF"
                    tooltipOptions={{ position: "top" }}
                    onClick={handleVerPdf}
                    disabled={busyId === ficha.id || loadingDownload}
                  />
                  <Button
                    icon="pi pi-download"
                    outlined
                    size="small"
                    tooltip="Descargar"
                    tooltipOptions={{ position: "top" }}
                    onClick={handleDescargar}
                    disabled={busyId === ficha.id || loadingDownload}
                  />
                  <Button
                    icon="pi pi-pencil"
                    outlined
                    size="small"
                    tooltip="Reemplazar"
                    tooltipOptions={{ position: "top" }}
                    onClick={startEdit}
                    disabled={busyId === ficha.id || editMode || loadingDownload}
                  />
                  <Button
                    icon="pi pi-trash"
                    outlined
                    severity="danger"
                    size="small"
                    tooltip="Eliminar"
                    tooltipOptions={{ position: "top" }}
                    onClick={handleEliminar}
                    disabled={busyId === ficha.id}
                    loading={busyId === ficha.id || eliminando || loadingDownload}
                  />
                </div>
              </div>
              <div className="text-sm text-500">
                {ficha.filename || "ficha.pdf"}
              </div>
            </div>

            {editMode || (
              <div className="border-1 border-300 border-round p-3 mb-3">
                <div className="flex align-items-center justify-content-between mb-2">
                  <span className="font-medium">Reemplazar ficha</span>
                  <Button
                    icon="pi pi-times"
                    className="p-button-text p-button-sm"
                    onClick={cancelEdit}
                    disabled={saving}
                  />
                </div>
                <input
                  ref={editFileInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={handleEditFileChange}
                  className="w-full mb-2"
                />
                <div className="flex gap-2">
                  <Button
                    icon="pi pi-save"
                    label="Guardar"
                    className="p-button-sm p-button-success"
                    loading={saving}
                    disabled={saving || !editFile}
                    onClick={handleModificar}
                  />
                </div>
              </div>
            )}

          </>
        ) : (
          <div className="field mb-4">
            <label className="block font-medium mb-2">
              Subir ficha técnica (.PDF maximo 2MB)
            </label>
            <FileUpload
              ref={fileUploadRef}
              customUpload
              uploadHandler={handleUpload}
              accept=".pdf,application/pdf"
              emptyTemplate={
                <p className="m-0">
                  Arrastra y suelta un archivo PDF para subirlo como ficha técnica del
                  producto.
                </p>
              }
              chooseLabel="Seleccionar"
              uploadLabel="Subir"
              cancelLabel="Cancelar"
              disabled={saving}
            />
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default FichaProductoDialog;